import React, { useEffect, useMemo, useRef, useState } from 'react';
import { applicationService } from 'src/services/application';
import { useApplicationStore } from 'src/store/application';
import { usePlan } from '@features/onboarding/hooks/usePlan';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';

import { AnimatedDots } from '@components/shared/animated-dots/AnimatedDots';
import { FormMessageSpan } from '@components/ui/form';
import { FloatingInput } from '@components/ui/input-floating';

import {
  AGREEMENT_SIGNATURE_MAX_LENGTH,
  normalizeAgreementSignature,
  validateAgreementSignature,
} from '@utils/agreementSignature';
import { cn } from '@utils/index';

import { usePaymentStore } from '../../store/payment';
import { StripeBanksElementForm } from './StripeBanksElementForm';

type PaymentStepProps = {
  /** callback чтобы Finish перешёл к следующему шагу */
  onSubmit: () => void;
  /** функция, позволяющая Finish задать классы внешнему контейнеру */
  setContainerClassName?: (className: string) => void;
  isSubmitting?: boolean;
  submissionError?: string | null;
  requiresAgreement?: boolean;
};

const stripContractDocumentTags = (html: string) => {
  const trimmedHtml = html.trim();
  if (!trimmedHtml) return '';

  const stripWithRegex = (value: string) =>
    value
      .replace(/<title\b[^>]*>[\s\S]*?<\/title>/gi, '')
      .replace(/<meta\b[^>]*\/?>/gi, '')
      .trim();

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return stripWithRegex(trimmedHtml);
  }

  const hasDocumentShell =
    /<!doctype/i.test(trimmedHtml) ||
    /<\/?(html|head|body)(\s|>)/i.test(trimmedHtml);

  if (!hasDocumentShell) {
    const template = document.createElement('template');
    template.innerHTML = trimmedHtml;
    template.content
      .querySelectorAll('title, meta')
      .forEach((element) => element.remove());

    return template.innerHTML.trim();
  }

  const parsedDocument = new DOMParser().parseFromString(
    trimmedHtml,
    'text/html'
  );
  parsedDocument
    .querySelectorAll('title, meta')
    .forEach((element) => element.remove());

  return `${parsedDocument.head.innerHTML}${parsedDocument.body.innerHTML}`.trim();
};

/**
 * Комбинированный шаг оплаты: слева текстовое сопровождение, справа – форма ACH.
 */
export const PaymentStep: React.FC<PaymentStepProps> = ({
  isSubmitting = false,
  submissionError = null,
  requiresAgreement = false,
}) => {
  const {
    paymentMethodId,
    contractSignatureName,
    setContractSignatureName,
    resetContractSignatureName,
  } = usePaymentStore();
  const currentApplicationId = useApplicationStore(
    (state) => state.currentApplicationId
  );
  const onboardingData = useOnboardingStore((state) => state.data);
  const { totalPlanPrice } = usePlan();
  const [currentStep, setCurrentStep] = useState<'connect' | 'submit'>(
    'connect'
  );
  const [contractHtml, setContractHtml] = useState<string | null>(null);
  const [isContractLoading, setIsContractLoading] = useState(false);
  const [signatureTouched, setSignatureTouched] = useState(false);
  const previousApplicationIdRef = useRef<number | string | undefined>(
    currentApplicationId
  );
  const prefilledSignatureApplicationRef = useRef<
    number | string | undefined
  >();
  const primaryApplicant =
    onboardingData.applicants?.find((a) => a.applicant === 'primary') ??
    onboardingData.applicants?.[0];
  const defaultSignatureName = primaryApplicant
    ? `${primaryApplicant.firstName ?? ''} ${primaryApplicant.lastName ?? ''}`.trim()
    : '';

  // Автоматически переключаемся на второй шаг когда банк подключен
  useEffect(() => {
    if (paymentMethodId) {
      setCurrentStep('submit');
    }
  }, [paymentMethodId]);

  useEffect(() => {
    const prefillKey = currentApplicationId ?? 'pending-application';

    if (!requiresAgreement) {
      if (contractSignatureName) {
        resetContractSignatureName();
      }
      setSignatureTouched(false);
      previousApplicationIdRef.current = currentApplicationId;
      prefilledSignatureApplicationRef.current = undefined;
      return;
    }

    if (
      currentApplicationId &&
      previousApplicationIdRef.current !== currentApplicationId
    ) {
      resetContractSignatureName();
      setSignatureTouched(false);
      prefilledSignatureApplicationRef.current = undefined;
      previousApplicationIdRef.current = currentApplicationId;
      return;
    }

    previousApplicationIdRef.current = currentApplicationId;

    if (contractSignatureName) {
      prefilledSignatureApplicationRef.current = prefillKey;
      return;
    }

    if (
      defaultSignatureName &&
      prefilledSignatureApplicationRef.current !== prefillKey
    ) {
      setContractSignatureName(defaultSignatureName);
      prefilledSignatureApplicationRef.current = prefillKey;
    }
  }, [
    contractSignatureName,
    currentApplicationId,
    defaultSignatureName,
    requiresAgreement,
    resetContractSignatureName,
    setContractSignatureName,
  ]);

  useEffect(() => {
    if (!requiresAgreement || !currentApplicationId) {
      setContractHtml(null);
      return;
    }

    let mounted = true;
    setIsContractLoading(true);
    applicationService
      .getContractHtml(currentApplicationId)
      .then((html) => {
        if (!mounted) return;
        const preparedHtml = stripContractDocumentTags(html);
        setContractHtml(preparedHtml || null);
      })
      .catch((error) => {
        console.warn('[PaymentStep] Failed to load contract HTML:', error);
        if (mounted) {
          setContractHtml(null);
        }
      })
      .finally(() => {
        if (mounted) {
          setIsContractLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [currentApplicationId, requiresAgreement]);

  const monthlyPremium = totalPlanPrice || 1102.01; // Fallback value
  const isAgreementReady = !requiresAgreement || Boolean(contractHtml);
  const signatureValidation = useMemo(
    () => validateAgreementSignature(contractSignatureName),
    [contractSignatureName]
  );
  const hasValidAgreementSignature =
    !requiresAgreement || (isAgreementReady && signatureValidation.isValid);
  const signatureError =
    requiresAgreement && signatureTouched && !hasValidAgreementSignature
      ? signatureValidation.error
      : null;
  const isFinalizingPayment = Boolean(paymentMethodId) || isSubmitting;
  const paymentInstruction = useMemo(() => {
    if (isFinalizingPayment) {
      return "We're finalizing your application. Please wait...";
    }
    if (currentStep !== 'connect') {
      return 'Bank connected! Click SUBMIT to finalize your payment.';
    }
    return 'Setting up your payment is simple. Just connect to your bank and then submit payment.';
  }, [currentStep, isFinalizingPayment]);

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-5xl space-y-8">
        {requiresAgreement && (
          <section className="space-y-5">
            <h1 className="text-2xl font-bold text-gray-950">
              Read and Sign Agreement
            </h1>
            <div className="max-h-[360px] min-h-[280px] overflow-y-auto rounded-lg border border-[#B7B7B7] bg-white px-5 py-4 shadow-inner">
              {isContractLoading ? (
                <p className="text-sm font-semibold text-gray-500">
                  Loading agreement...
                </p>
              ) : contractHtml ? (
                <div dangerouslySetInnerHTML={{ __html: contractHtml }} />
              ) : (
                <p className="text-sm font-semibold text-red-600">
                  Agreement could not be loaded. Please try again later.
                </p>
              )}
            </div>
            <div className="flex flex-col gap-3 md:flex-row md:items-start">
              <label
                htmlFor="contractSignatureName"
                className="pt-3 text-base font-bold text-gray-950 md:shrink-0"
              >
                Type your name to accept:
              </label>
              <div className="w-full max-w-96">
                <FloatingInput
                  id="contractSignatureName"
                  label="Full name"
                  type="text"
                  value={contractSignatureName}
                  onChange={(event) =>
                    setContractSignatureName(
                      event.target.value.replace(/^\s+/, '')
                    )
                  }
                  onBlur={() => {
                    setSignatureTouched(true);
                    const normalizedSignature = normalizeAgreementSignature(
                      contractSignatureName
                    );
                    if (contractSignatureName !== normalizedSignature) {
                      setContractSignatureName(normalizedSignature);
                    }
                  }}
                  maxLength={AGREEMENT_SIGNATURE_MAX_LENGTH}
                  autoComplete="name"
                  disabled={!isAgreementReady || isContractLoading}
                  aria-invalid={!!signatureError}
                  wrapperClass="max-w-auto"
                  inputClass={cn(
                    'h-13.5 xl:!h-16.5 min-h-auto outline-none border border-[#B7B7B7] rounded-lg px-3 max-md:text-[18px]',
                    (!isAgreementReady || isContractLoading) &&
                      'cursor-not-allowed bg-gray-100 text-gray-400',
                    signatureError && '!border-red-500 !border-2'
                  )}
                />
                {signatureError && (
                  <FormMessageSpan
                    className="mt-2 block text-center"
                    text={signatureError}
                  />
                )}
              </div>
            </div>
          </section>
        )}

        {requiresAgreement && <div className="h-px w-full bg-gray-200" />}

        <section className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-950">
              Enter payment information
            </h2>
            <p className="mt-2 text-base font-semibold text-gray-900">
              Your monthly premium: ${monthlyPremium.toFixed(2)}
            </p>
          </div>

          <div className="rounded-lg border border-[#E2DED8] bg-[#f7f6f2] p-4 sm:p-8">
            {/* Инструкция */}
            <div className="text-center mb-6 sm:mb-8">
              <p className="text-base sm:text-lg text-primary font-semibold">
                {paymentInstruction}
              </p>
            </div>

            {/* Прогресс бар с двумя шагами */}
            <div className="flex items-center justify-center mb-8 w-full px-4 sm:px-8">
              <div className="bg-[#f1ede7] rounded-xl p-4 w-full">
                <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                  {/* Шаг 1: Connect to bank */}
                  <div
                    className={cn(
                      'flex items-center justify-center space-x-3 px-4 sm:px-6 py-3 w-full sm:w-auto',
                      currentStep === 'connect'
                        ? 'text-primary'
                        : 'text-gray-500'
                    )}
                  >
                    <div
                      className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0',
                        currentStep === 'connect'
                          ? 'bg-primary text-white'
                          : 'bg-gray-400 text-white'
                      )}
                    >
                      1
                    </div>
                    <span
                      className={cn(
                        'font-medium text-center sm:text-left',
                        currentStep === 'connect'
                          ? 'font-bold text-primary text-lg sm:text-xl'
                          : 'font-normal text-gray-500 text-lg sm:text-xl'
                      )}
                    >
                      Connect to bank
                    </span>
                  </div>

                  {/* Разделитель */}
                  <div className="hidden sm:block w-px h-10 bg-gray-300 mx-2"></div>
                  <div className="sm:hidden w-full h-px bg-gray-300"></div>

                  {/* Шаг 2: Submit payment */}
                  <div
                    className={cn(
                      'flex items-center justify-center space-x-3 px-4 sm:px-6 py-3 w-full sm:w-auto',
                      currentStep === 'submit'
                        ? 'text-primary'
                        : 'text-gray-500'
                    )}
                  >
                    <div
                      className={cn(
                        'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0',
                        currentStep === 'submit'
                          ? 'bg-primary text-white'
                          : 'bg-[#cbbfb1] text-white'
                      )}
                    >
                      2
                    </div>
                    <span
                      className={cn(
                        'font-medium text-center sm:text-left',
                        currentStep === 'submit'
                          ? 'font-bold text-primary text-lg sm:text-xl'
                          : 'font-bold text-[#cbbfb1] text-lg sm:text-xl'
                      )}
                    >
                      Submit payment
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mb-6 sm:mb-8">
              <p className="text-base sm:text-lg text-primary font-semibold">
                {requiresAgreement && !hasValidAgreementSignature && (
                  <span className="text-primary font-semibold">
                    Type your name to accept the agreement before entering
                    payment information.
                  </span>
                )}
              </p>
              {submissionError && !isSubmitting && (
                <p className="text-base sm:text-lg text-red-600 font-semibold mt-4">
                  {submissionError}
                </p>
              )}
            </div>

            {/* Форма или подтверждение */}
            <div className="flex flex-col justify-center">
              <div className="w-fit max-w-2xl m-auto">
                {isFinalizingPayment ? (
                  <div className="min-h-24 w-full min-w-72 px-5 py-6 text-center text-sm font-semibold text-gray-500">
                    <p>Finalizing your application...</p>
                    <AnimatedDots />
                  </div>
                ) : hasValidAgreementSignature ? (
                  <StripeBanksElementForm
                    setCurrentStep={setCurrentStep}
                    isSubmitting={isSubmitting}
                  />
                ) : (
                  <div className="min-h-24 w-full min-w-72 rounded-lg border border-dashed border-[#CFC7BC] bg-white/70 px-5 py-6 text-center text-sm font-semibold text-gray-500">
                    {requiresAgreement && !isAgreementReady
                      ? 'Payment fields will appear after the agreement is loaded and accepted.'
                      : 'Payment fields will appear after the agreement is accepted.'}
                  </div>
                )}
              </div>
              {/* {currentStep !== 'connect' && (
              <div className="w-full max-w-2xl">
                <div className="mt-6 text-center">
                  <button
                    onClick={onSubmit}
                    className="bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
                  >
                    SUBMIT
                  </button>
                </div>
              </div>
            )} */}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
